import React, { useState, useEffect } from "react";
import { Loader2, Music, Heart, Star, Zap, Sparkles } from "lucide-react";
import "./styles.scss";

export interface MegaLoaderProps {
  size?: "small" | "medium" | "large" | "xl";
  variant?: "default" | "music" | "heart" | "star" | "zap" | "sparkles";
  text?: string;
  showProgress?: boolean;
  progress?: number;
  className?: string;
  fullScreen?: boolean;
  overlay?: boolean;
}

const MegaLoader: React.FC<MegaLoaderProps> = ({
  size = "medium",
  variant = "default",
  text = "",
  showProgress = false,
  progress = 0,
  className = "",
  fullScreen = false,
  overlay = false,
}) => {
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (showProgress && progress !== undefined) {
      setCurrentProgress(progress);
    }
  }, [progress, showProgress]);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const getIcon = () => {
    switch (variant) {
      case "music":
        return <Music className="w-full h-full" />;
      case "heart":
        return <Heart className="w-full h-full" />;
      case "star":
        return <Star className="w-full h-full" />;
      case "zap":
        return <Zap className="w-full h-full" />;
      case "sparkles":
        return <Sparkles className="w-full h-full" />;
      default:
        return <Loader2 className="w-full h-full" />;
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "small":
        return "w-16 h-16";
      case "medium":
        return "w-24 h-24";
      case "large":
        return "w-32 h-32";
      case "xl":
        return "w-40 h-40";
      default:
        return "w-24 h-24";
    }
  };

  const getTextSize = () => {
    switch (size) {
      case "small":
        return "text-sm";
      case "medium":
        return "text-base";
      case "large":
        return "text-lg";
      case "xl":
        return "text-xl";
      default:
        return "text-base";
    }
  };

  const loaderContent = (
    <div className={`mega-loader ${getSizeClasses()} ${className}`}>
      {/* Main Loading Icon */}
      <div className="loader-icon-container">
        <div className="loader-icon">{getIcon()}</div>

        {/* Rotating Rings */}
        <div className="loader-rings">
          <div className="ring ring-1"></div>
          <div className="ring ring-2"></div>
          <div className="ring ring-3"></div>
        </div>

        {/* Floating Particles */}
        <div className="particles">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={
                {
                  "--delay": `${i * 0.2}s`,
                  "--angle": `${i * 60}deg`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        {/* Pulse Effect */}
        <div className="pulse-effect"></div>
      </div>

      {/* Loading Text */}
      {text && (
        <div className={`loader-text ${getTextSize()}`}>
          <span className="text-content">{text}</span>
          <div className="text-dots">
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {showProgress && (
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${currentProgress}%` }}
            />
          </div>
          <div className="progress-text">{Math.round(currentProgress)}%</div>
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    return <div className="mega-loader-fullscreen">{loaderContent}</div>;
  }

  if (overlay) {
    return <div className="mega-loader-overlay">{loaderContent}</div>;
  }

  return loaderContent;
};

export default MegaLoader;
